import React, { useState, useEffect } from 'react';
import { SPM4_SCENARIOS, SPM_FIGURE_METADATA } from '../../data/ipccData';
import { AudioEngine } from '../../audio/AudioEngine';
import { FigureDescriptionPanel } from '../FigureDescriptionPanel';
import { Layers, Eye, EyeOff, Play, Pause, Thermometer, Waves, Sparkles } from 'lucide-react';

interface SPM4ScenariosProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
}

export const SPM4Scenarios: React.FC<SPM4ScenariosProps> = ({ isPlaying, onTogglePlay }) => {
  const meta = SPM_FIGURE_METADATA['spm4'];
  const audioEngine = AudioEngine.getInstance();

  const [activeScenarios, setActiveScenarios] = useState<string[]>([
    'ssp1_19',
    'ssp1_26',
    'ssp2_45',
    'ssp3_70',
    'ssp5_85'
  ]);
  const [timeStepIndex, setTimeStepIndex] = useState<number>(0);

  const years = SPM4_SCENARIOS[0].trajectory.map((t) => t.year);

  // Auto-play timeline loop
  useEffect(() => {
    let timer: number;
    if (isPlaying) {
      timer = window.setInterval(() => {
        setTimeStepIndex((prev) => (prev + 1) % years.length);
      }, 1000);
    } else {
      audioEngine.stopAll();
    }
    return () => clearInterval(timer);
  }, [isPlaying, years.length]);

  // Update Audio based on active scenarios & selected timestep
  useEffect(() => {
    if (!audioEngine.getIsUnlocked() || !isPlaying) return;

    // Pick highest active scenario for primary pitch & noise decay
    const activeObj = SPM4_SCENARIOS.filter((s) => activeScenarios.includes(s.id));
    if (activeObj.length === 0) return;

    // Highest warming scenario amongst active
    const topScenario = activeObj[activeObj.length - 1];
    const pt = topScenario.trajectory[timeStepIndex];

    // Primary pitch mapping
    const freq = audioEngine.tempToFreq(pt.temp);
    audioEngine.startAnthropoPulse(freq, 4 + pt.temp * 2, 0.35);

    // Sea Ice Decay (Cryo-Hiss patch): as sea ice (pt.seaIce) decreases from 4.3 -> 0, high-pass cutoff rises and volume drops
    if (pt.seaIce > 0.2) {
      const seaIceVolume = Math.min(0.3, (pt.seaIce / 4.5) * 0.3);
      audioEngine.startCryoHiss(6000 + (4.3 - pt.seaIce) * 2000, seaIceVolume);
    } else {
      audioEngine.stopCryoHiss();
    }
  }, [timeStepIndex, activeScenarios, isPlaying]);

  const toggleScenario = (id: string) => {
    setActiveScenarios((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const activeYear = years[timeStepIndex];

  return (
    <div className="space-y-6">
      {/* Title Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 glass-panel p-5 rounded-2xl border border-slate-800">
        <div>
          <span className="text-xs font-mono text-cyan-400 font-semibold">{meta.number}</span>
          <h2 className="text-xl font-extrabold text-white">{meta.title}</h2>
          <p className="text-xs text-slate-400 mt-1">{meta.subtitle}</p>
        </div>

        <button
          onClick={onTogglePlay}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-lg ${
            isPlaying ? 'bg-amber-500 text-slate-950 glow-amber' : 'bg-cyan-500 text-slate-950 glow-cyan'
          }`}
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          <span>{isPlaying ? 'Pause Scenarios' : 'Play Polyphonic Divergence'}</span>
        </button>
      </div>

      {/* Main Interactive Module */}
      <div className="glass-panel p-6 rounded-3xl border border-slate-800 space-y-6">
        {/* Scenarios Selector Toggles */}
        <div className="space-y-2">
          <span className="text-xs font-mono text-slate-400 block font-semibold">
            Toggle Shared Socioeconomic Pathways (SSPs) for Polyphonic Divergence:
          </span>
          <div className="flex flex-wrap gap-2">
            {SPM4_SCENARIOS.map((scenario) => {
              const isActive = activeScenarios.includes(scenario.id);
              return (
                <button
                  key={scenario.id}
                  onClick={() => toggleScenario(scenario.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${
                    isActive
                      ? 'bg-slate-800 text-white border-cyan-400 shadow-md'
                      : 'bg-slate-950/60 text-slate-500 border-slate-800 hover:text-slate-300'
                  }`}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: scenario.color }}
                  ></span>
                  <span>{scenario.name.split(' ')[0]}</span>
                  {isActive ? <Eye className="w-3.5 h-3.5 text-cyan-400" /> : <EyeOff className="w-3.5 h-3.5 text-slate-600" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Timeline Scrubber */}
        <div className="space-y-2 pt-2">
          <div className="flex justify-between text-xs font-mono text-slate-400">
            <span>2015</span>
            <span className="text-cyan-300 font-bold">Selected Horizon: Year {activeYear}</span>
            <span>2100</span>
          </div>
          <input
            type="range"
            min="0"
            max={years.length - 1}
            value={timeStepIndex}
            onChange={(e) => setTimeStepIndex(parseInt(e.target.value))}
            className="w-full h-2 rounded-lg bg-slate-800 accent-cyan-400 cursor-pointer"
          />
        </div>

        {/* Trajectory Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
          {SPM4_SCENARIOS.filter((s) => activeScenarios.includes(s.id)).map((scenario) => {
            const pt = scenario.trajectory[timeStepIndex];
            return (
              <div
                key={scenario.id}
                className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-3 glass-panel-hover"
              >
                <div className="flex items-center justify-between">
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-md text-white font-mono"
                    style={{ backgroundColor: scenario.color }}
                  >
                    {scenario.name.split(' ')[0]}
                  </span>
                  <span className="text-[10px] font-mono text-slate-400">Year {activeYear}</span>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between text-slate-200">
                    <span className="flex items-center gap-1.5 text-red-400">
                      <Thermometer className="w-4 h-4" /> Temp Anomaly:
                    </span>
                    <span className="font-mono font-bold text-white">+{pt.temp.toFixed(1)}°C</span>
                  </div>

                  <div className="flex items-center justify-between text-slate-200">
                    <span className="flex items-center gap-1.5 text-cyan-400">
                      <Sparkles className="w-4 h-4" /> Sept Arctic Sea Ice:
                    </span>
                    <span className="font-mono font-bold text-white">{pt.seaIce.toFixed(1)} M km²</span>
                  </div>

                  <div className="flex items-center justify-between text-slate-200">
                    <span className="flex items-center gap-1.5 text-blue-400">
                      <Waves className="w-4 h-4" /> Ocean Surface pH:
                    </span>
                    <span className="font-mono font-bold text-white">{pt.pH.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Scientific Synopsis & Original Figure Reference Panel */}
      <FigureDescriptionPanel meta={meta} />
    </div>
  );
};
